import { Loader2, Trash2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { APPLICATION_STATUSES, STATUS_COLOR } from "../../lib/applicationStatus";

const FIELDS = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "dob", label: "DOB" },
  { key: "reason_for_loan", label: "Reason for Loan", full: true },
  { key: "b_name", label: "B Name" },
  { key: "r_name", label: "R Name" },
  { key: "a_name", label: "A Name" },
  { key: "identifier", label: "Identifier" },
  { key: "identification", label: "Identification" },
];

export default function ApplicationDetailDialog({ app, open, onClose, onUpdateStatus, updating, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl rounded-none border-white/15 bg-[#0A1128] text-white"
        data-testid="application-detail-dialog"
      >
        {app && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl font-light">
                {app.first_name} {app.last_name}
              </DialogTitle>
              <DialogDescription className="text-white/50">Reference {app.id}</DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <Detail key={f.key} label={f.label} value={app[f.key]} full={f.full} />
              ))}
              <Detail label="Submitted" value={new Date(app.created_at).toLocaleString()} />
            </div>

            <div className="mt-6 border-t border-white/10 pt-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Advance Application</div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span
                  className={`inline-block border px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] ${
                    STATUS_COLOR[app.status] || ""
                  }`}
                  data-testid="dialog-current-status"
                >
                  Current: {app.status}
                </span>
                <Select
                  value={app.status}
                  onValueChange={(v) => onUpdateStatus(app.id, v)}
                  disabled={updating}
                >
                  <SelectTrigger
                    data-testid="dialog-status-select"
                    className="w-56 rounded-none border-white/15 bg-[#050A1F] text-sm text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/15 bg-[#0A1128] text-white">
                    {APPLICATION_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {updating && <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />}
              </div>
              <p className="mt-3 text-xs text-white/50">
                Set to <span className="text-[#D4AF37]">Funds Transferred</span> once the wire has cleared.
              </p>
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-red-500/15 pt-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-red-400/80">Danger Zone</div>
                <p className="mt-1 text-xs text-white/50">
                  Permanently remove this application. This action cannot be undone.
                </p>
              </div>
              <button
                onClick={() => onDelete(app)}
                data-testid="dialog-delete-btn"
                className="inline-flex items-center gap-2 border border-red-400/40 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-red-400 transition-colors hover:border-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} /> Delete
              </button>
            </div>

            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-white/50 hover:text-white"
              data-testid="dialog-close-btn"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={`border border-white/10 bg-[#050A1F] p-4 ${full ? "sm:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">{label}</div>
      <div className="mt-1 break-words text-sm text-white/90">{value}</div>
    </div>
  );
}
